import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import { useAuthStore } from '../lib/auth'
import { supabase } from '../lib/supabase'
import { cancelSubscription, purchaseCredits, getCreditHistory, createPortalSession } from '../lib/api'
import { User, Lock, Zap, CreditCard, AlertTriangle, Save, ExternalLink, Trash2, ShieldAlert, Loader2, XCircle } from 'lucide-react'

export default function Settings() {
  const navigate = useNavigate()
  const { user, profile, logout } = useAuthStore()
  const updateProfile = useAuthStore((s) => s.updateProfile)
  const refreshProfile = useAuthStore((s) => s.refreshProfile)
  const getSubscriptionInfo = useAuthStore((s) => s.getSubscriptionInfo)
  const getCreditsInfo = useAuthStore((s) => s.getCreditsInfo)

  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [creditHistory, setCreditHistory] = useState([])
  const [creditHistoryLoading, setCreditHistoryLoading] = useState(false)
  const [buyingCredits, setBuyingCredits] = useState(null)
  const [portalLoading, setPortalLoading] = useState(false)

  // Profile settings - populated from real data
  const [profileData, setProfileData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    city: '',
    state: ''
  })

  // Password change
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  })

  // Load profile data when available
  useEffect(() => {
    if (profile) {
      setProfileData({
        username: profile.username || '',
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        email: profile.email || user?.email || '',
        city: profile.city || '',
        state: profile.state || ''
      })
    }
  }, [profile, user])

  const subInfo = getSubscriptionInfo()

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      // Update username if changed
      if (profileData.username !== profile?.username) {
        await updateProfile({ username: profileData.username })
      }

      // Update other profile fields directly
      const { error: updateError } = await supabase
        .from('users')
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          city: profileData.city,
          state: profileData.state
        })
        .eq('id', user.id)

      if (updateError) throw new Error(updateError.message)

      setSuccess('Profile updated successfully')
    } catch (err) {
      setError(err.message || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      const { error: pwError } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      })
      if (pwError) throw new Error(pwError.message)
      setSuccess('Password changed successfully')
      setPasswordData({ newPassword: '', confirmPassword: '' })
    } catch (err) {
      setError(err.message || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return
    }

    if (!window.confirm('This will permanently delete all your data. Are you absolutely sure?')) {
      return
    }

    setLoading(true)

    try {
      // Delete user profile data
      await supabase.from('user_aliases').delete().eq('user_id', user.id)
      await supabase.from('scans').delete().eq('user_id', user.id)
      await supabase.from('api_keys').delete().eq('user_id', user.id)
      await supabase.from('users').delete().eq('id', user.id)

      await logout()
      navigate('/login')
    } catch (err) {
      setError('Failed to delete account: ' + err.message)
      setLoading(false)
    }
  }

  const handleCancelSubscription = async () => {
    setCancelLoading(true)
    setError('')
    setSuccess('')

    try {
      await cancelSubscription()
      await refreshProfile()
      setShowCancelConfirm(false)
      setSuccess('Subscription cancelled successfully.')
    } catch (err) {
      setError(err.message || 'Failed to cancel subscription.')
    } finally {
      setCancelLoading(false)
    }
  }

  const canCancel = subInfo.plan === 'basic' || subInfo.plan === 'premium' || subInfo.isTrial

  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: "'Orbitron', sans-serif" }}>
          Account Settings
        </h1>

        {/* Subscription Status Banner */}
        <div className="card mb-6 border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">Subscription Status</h3>
              <p className="text-gray-400 text-sm mt-1">
                {subInfo.plan === 'premium' && !subInfo.isTrial && 'Premium Plan - Full access'}
                {subInfo.isTrial && `Basic Plan - Free Trial (${subInfo.daysRemaining} days remaining)`}
                {subInfo.plan === 'basic' && !subInfo.isTrial && !subInfo.isPremium && 'Basic Plan - Limited features'}
                {subInfo.isCancelled && 'Cancelled - Access until end of billing period'}
              </p>
            </div>
            <div className="flex gap-3">
              {canCancel && !subInfo.isCancelled && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="text-sm text-gray-400 hover:text-red-400 transition border border-white/10 hover:border-red-500/30 px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Plan
                </button>
              )}
              {(subInfo.plan !== 'premium' || subInfo.isCancelled) && (
                <button
                  onClick={() => navigate('/plans')}
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <Zap className="w-4 h-4" />
                  {subInfo.isCancelled ? 'Re-subscribe' : 'Upgrade'}
                </button>
              )}
            </div>
          </div>
          {profile?.username && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-gray-500 text-sm">
                HireMail Alias: <span className="text-cyan-400">{profile.username}@hiremail.net</span>
              </p>
            </div>
          )}
        </div>

        {/* Cancel Subscription Confirmation Dialog */}
        {showCancelConfirm && (
          <div className="card mb-6 border border-red-500/30 bg-red-500/5">
            <h3 className="text-red-400 font-semibold mb-2">Cancel Subscription?</h3>
            <p className="text-gray-400 text-sm mb-4">
              Are you sure you want to cancel? You'll lose access to premium features
              and your plan will be downgraded.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel My Plan'}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelLoading}
                className="text-gray-400 hover:text-white px-5 py-2 rounded-lg border border-white/10 transition text-sm"
              >
                Keep My Plan
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-500/20 text-green-400 p-4 rounded-lg mb-6 border border-green-500/30">
            {success}
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 text-red-400 p-4 rounded-lg mb-6 border border-red-500/30">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10 overflow-x-auto">
          <button
            onClick={() => { setActiveTab('profile'); setError(''); setSuccess('') }}
            className={`pb-3 px-4 font-semibold transition flex items-center whitespace-nowrap ${activeTab === 'profile'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-gray-400 hover:text-white'
              }`}
          >
            <User className="w-4 h-4 mr-2" />
            Profile
          </button>
          <button
            onClick={() => { setActiveTab('password'); setError(''); setSuccess('') }}
            className={`pb-3 px-4 font-semibold transition flex items-center whitespace-nowrap ${activeTab === 'password'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-gray-400 hover:text-white'
              }`}
          >
            <Lock className="w-4 h-4 mr-2" />
            Password
          </button>
          <button
            onClick={() => {
              setActiveTab('credits')
              setError('')
              setSuccess('')
              setCreditHistoryLoading(true)
              getCreditHistory(30).then(setCreditHistory).catch(console.error).finally(() => setCreditHistoryLoading(false))
            }}
            className={`pb-3 px-4 font-semibold transition flex items-center whitespace-nowrap ${activeTab === 'credits'
              ? 'text-emerald-400 border-b-2 border-emerald-400'
              : 'text-gray-400 hover:text-white'
              }`}
          >
            <Zap className="w-4 h-4 mr-2" />
            Credits
          </button>
          <button
            onClick={() => { setActiveTab('billing'); setError(''); setSuccess('') }}
            className={`pb-3 px-4 font-semibold transition flex items-center whitespace-nowrap ${activeTab === 'billing'
              ? 'text-amber-400 border-b-2 border-amber-400'
              : 'text-gray-400 hover:text-white'
              }`}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Billing
          </button>
          <button
            onClick={() => { setActiveTab('danger'); setError(''); setSuccess('') }}
            className={`pb-3 px-4 font-semibold transition flex items-center whitespace-nowrap ${activeTab === 'danger'
              ? 'text-red-400 border-b-2 border-red-400'
              : 'text-gray-400 hover:text-white'
              }`}
          >
            <ShieldAlert className="w-4 h-4 mr-2" />
            Danger Zone
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="card">
            <h2 className="text-2xl font-bold text-white mb-6">Profile Information</h2>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">First Name</label>
                  <input
                    type="text"
                    className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">Last Name</label>
                  <input
                    type="text"
                    className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-2 text-sm">
                  Username <span className="text-gray-500">(your @hiremail.net alias)</span>
                </label>
                <input
                  type="text"
                  className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-2 text-sm">Email Address</label>
                <input
                  type="email"
                  className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-gray-500 cursor-not-allowed"
                  value={profileData.email}
                  disabled
                />
                <p className="text-gray-600 text-xs mt-1">Email cannot be changed</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">City</label>
                  <input
                    type="text"
                    className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                    value={profileData.city}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">State</label>
                  <input
                    type="text"
                    className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                    value={profileData.state}
                    onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="card">
            <h2 className="text-2xl font-bold text-white mb-6">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-gray-400 mb-2 text-sm">New Password</label>
                <input
                  type="password"
                  className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-2 text-sm">Confirm New Password</label>
                <input
                  type="password"
                  className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  placeholder="Re-enter new password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {/* Credits Tab */}
        {activeTab === 'credits' && (
          <div className="space-y-6">
            {/* Credit Balance Card */}
            <div className="card">
              <h2 className="text-2xl font-bold text-white mb-4">Credit Balance</h2>
              {(() => {
                const credits = getCreditsInfo()
                return (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-400">{credits.total}</p>
                      <p className="text-gray-400 text-sm mt-1">Total Available</p>
                    </div>
                    <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-cyan-400">{credits.monthly}</p>
                      <p className="text-gray-400 text-sm mt-1">Monthly ({credits.planAllocation}/mo)</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-amber-400">{credits.purchased}</p>
                      <p className="text-gray-400 text-sm mt-1">Purchased (never expire)</p>
                    </div>
                  </div>
                )
              })()}

              <h3 className="text-white font-semibold mb-3">Buy More Credits</h3>
              <div className="flex gap-3">
                {[
                  { qty: 1, credits: 100, price: '$1' },
                  { qty: 2, credits: 200, price: '$2' },
                  { qty: 5, credits: 500, price: '$5' },
                ].map((pack) => (
                  <button
                    key={pack.qty}
                    onClick={async () => {
                      setBuyingCredits(pack.qty)
                      try {
                        const { url } = await purchaseCredits(pack.qty)
                        if (url) window.location.href = url
                      } catch (err) {
                        setError(err.message)
                        setBuyingCredits(null)
                      }
                    }}
                    disabled={buyingCredits !== null}
                    className="flex-1 p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/15 transition text-center disabled:opacity-50"
                  >
                    <p className="text-emerald-400 font-semibold">{pack.credits}</p>
                    <p className="text-gray-500 text-xs">
                      {buyingCredits === pack.qty ? 'Redirecting...' : pack.price}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Transaction History */}
            <div className="card">
              <h2 className="text-2xl font-bold text-white mb-4">Transaction History</h2>
              {creditHistoryLoading ? (
                <p className="text-gray-500">Loading...</p>
              ) : creditHistory.length === 0 ? (
                <p className="text-gray-500">No credit transactions yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-gray-400">
                        <th className="text-left py-2 pr-4">Date</th>
                        <th className="text-left py-2 pr-4">Type</th>
                        <th className="text-left py-2 pr-4">Description</th>
                        <th className="text-right py-2 pr-4">Amount</th>
                        <th className="text-right py-2">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditHistory.map((tx) => (
                        <tr key={tx.id} className="border-b border-white/5 text-gray-300">
                          <td className="py-2 pr-4 text-gray-500">
                            {new Date(tx.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-2 pr-4">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${tx.type === 'scan' ? 'bg-red-500/10 text-red-400' :
                              tx.type === 'purchase' ? 'bg-emerald-500/10 text-emerald-400' :
                                tx.type === 'monthly_reset' ? 'bg-cyan-500/10 text-cyan-400' :
                                  'bg-white/10 text-gray-400'
                              }`}>
                              {tx.type.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-2 pr-4">{tx.description}</td>
                          <td className={`py-2 pr-4 text-right font-mono ${tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount}
                          </td>
                          <td className="py-2 text-right font-mono text-gray-400">{tx.balance_after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-2xl font-bold text-white mb-4">Billing &amp; Invoices</h2>
              <p className="text-gray-400 mb-6">
                View your invoices, update payment methods, and manage your subscription
                through Stripe's secure billing portal.
              </p>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">Current Plan</p>
                    <p className="text-gray-400 text-sm capitalize">
                      {subInfo.isTrial ? 'Basic (Free Trial)' : subInfo.plan}
                      {subInfo.isCancelled && ' (Cancelled)'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">
                      {subInfo.plan === 'premium' ? '$19.99' : subInfo.plan === 'basic' ? '$9.99' : 'Free'}
                    </p>
                    <p className="text-gray-500 text-sm">per month</p>
                  </div>
                </div>
              </div>


              <button
                onClick={async () => {
                  setPortalLoading(true)
                  setError('')
                  try {
                    const { url } = await createPortalSession()
                    if (url) window.location.href = url
                  } catch (err) {
                    setError(err.message || 'Failed to open billing portal')
                  } finally {
                    setPortalLoading(false)
                  }
                }}
                disabled={portalLoading || !profile?.stripe_customer_id}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                {portalLoading ? 'Opening Portal...' : 'Manage Billing'}
              </button>

              {!profile?.stripe_customer_id && (
                <p className="text-gray-500 text-sm mt-3">
                  Billing portal is available after subscribing to a plan.{' '}
                  <button onClick={() => navigate('/plans')} className="text-cyan-400 hover:text-cyan-300 transition">
                    View Plans
                  </button>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Danger Zone Tab */}
        {activeTab === 'danger' && (
          <div className="card border-red-500/30">
            <h2 className="text-2xl font-bold text-red-400 mb-4">Danger Zone</h2>
            <p className="text-gray-400 mb-6">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <button
              onClick={handleDeleteAccount}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {loading ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
